import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import HeaderNavigation from '../../../components/ui/HeaderNavigation';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import PunchInSummary from './components/PunchInSummary';
import CameraCapture from './components/CameraCapture';
import LocationVerification from './components/LocationVerification';
import WorkDurationSummary from './components/WorkDurationSummary';
import attendanceService from '../../../services/attendanceService';
import employeeService from '../../../services/employeeService';
import NotificationToast from '../../../components/ui/NotificationToast';
import { getPunchState } from '../../../utils/attendanceHelpers';

const SelfiePunchOut = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [capturedImage, setCapturedImage] = useState(null);
  const [imageBlob, setImageBlob] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [punchInData, setPunchInData] = useState(null);
  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadEmployeeData();
  }, [userProfile]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const loadEmployeeData = async () => {
    if (!userProfile?.email) return;

    try {
      setLoading(true);
      const result = await employeeService.getEmployees({ search: userProfile.email });
      if (result.success && result.data && result.data.length > 0) {
        const emp = result.data[0];
        setEmployee(emp);
        await loadTodayStatus(emp.id);
      } else {
        setValidationError('Employee record not found. Please contact HR.');
      }
    } catch (err) {
      console.error('Error loading employee data:', err);
      setValidationError('Failed to load employee data');
    } finally {
      setLoading(false);
    }
  };

  const loadTodayStatus = async (employeeId) => {
    try {
      const result = await attendanceService.getTodayStatus(employeeId);
      if (result.success && result.data) {
        const punchState = getPunchState(result.data);

        if (!punchState.canPunchOut) {
          setValidationError(
            punchState.canPunchIn
              ? 'You have not punched in. Please punch in first.'
              : 'No active punch-in session. You have already completed attendance for today.'
          );
          return;
        }

        const currentInTime = result.data.current_punch_in_time || result.data.record?.clock_in_time;
        const currentLocation = result.data.current_punch_in_location
          || result.data.record?.clock_in_location_address
          || 'Location not available';

        if (currentInTime) {
          setPunchInData({
            employeeId: employeeId,
            punchInTime: new Date(currentInTime),
            latitude: result.data.record?.clock_in_latitude ? parseFloat(result.data.record.clock_in_latitude) : null,
            longitude: result.data.record?.clock_in_longitude ? parseFloat(result.data.record.clock_in_longitude) : null,
            address: currentLocation,
            selfieUrl: result.data.record?.clock_in_photo_url
          });
        }
      }
    } catch (err) {
      console.error('Error loading today status:', err);
      setValidationError('Failed to load today\'s attendance status');
    }
  };

  const handleImageCapture = (imageUrl, blob) => {
    setCapturedImage(imageUrl);
    setImageBlob(blob);
    setValidationError(null);
  };

  const handleLocationCapture = (location) => {
    setCurrentLocation(location);
    setValidationError(null);
  };

  const convertBlobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const handlePunchOut = async () => {
    if (!capturedImage || !imageBlob) {
      setValidationError('Please capture your selfie before punching out.');
      return;
    }

    if (!currentLocation) {
      setValidationError('Location verification is required. Please enable location access.');
      return;
    }

    if (!employee || !punchInData) {
      setValidationError('Attendance data not loaded. Please refresh the page.');
      return;
    }

    setIsSubmitting(true);
    setValidationError(null);

    try {
      // Convert blob to base64
      const photoBase64 = await convertBlobToBase64(imageBlob);

      const punchOutData = {
        employee_id: employee.id,
        photo: photoBase64,
        latitude: currentLocation.latitude,
        longitude: currentLocation.longitude,
        location: {
          latitude: currentLocation.latitude,
          longitude: currentLocation.longitude,
          accuracy: currentLocation.accuracy,
          address: currentLocation.address
        },
        location_address: currentLocation.address,
        location_accuracy: currentLocation.accuracy,
        entry_method: 'selfie'
      };

      const result = await attendanceService.clockOut(punchOutData);

      if (result.success) {
        setNotification({ message: 'Punched out successfully!', type: 'success' });
        setTimeout(() => {
          navigate('/erp/employee-self-service', { state: { activeTab: 'attendance' } });
        }, 1500);
      } else {
        setNotification({ message: result.error || 'Failed to punch out', type: 'error' });
        setIsSubmitting(false);
      }
    } catch (err) {
      console.error('Error punching out:', err);
      setNotification({ message: 'Failed to punch out. Please try again.', type: 'error' });
      setIsSubmitting(false);
    }
  };

  const currentDuration = punchInData ? currentTime - new Date(punchInData.punchInTime) : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (validationError && !punchInData) {
    return (
      <div className="min-h-screen bg-background">
        <HeaderNavigation
          user={userProfile}
          notifications={[]}
          showSidebarToggle={false}
        />
        <main className="pt-16">
          <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-4">
            <div className="max-w-md w-full bg-card rounded-xl shadow-elevation-3 p-6 md:p-8 text-center border border-border">
              <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-error/10 flex items-center justify-center mx-auto mb-4">
                <Icon name="AlertTriangle" size={32} className="md:w-10 md:h-10 text-error" />
              </div>
              <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-2">No Active Session</h2>
              <p className="text-sm md:text-base text-muted-foreground mb-6">{validationError}</p>
              <Button
                onClick={() => navigate('/erp/selfie-attendance/punch-in')}
                iconName="LogIn"
                iconPosition="left"
                fullWidth
              >
                Go to Punch In
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <HeaderNavigation
        user={userProfile}
        notifications={[]}
        showSidebarToggle={false}
      />

      <main className="pt-16">
        <div className="max-w-7xl mx-auto p-4 md:p-6">
          {notification && (
            <NotificationToast
              message={notification.message}
              type={notification.type}
              onDismiss={() => setNotification(null)}
              position="top-right"
              autoHideDuration={3000}
            />
          )}

          <div className="mb-6 md:mb-8">
            <button
              onClick={() => navigate('/erp/employee-self-service', { state: { activeTab: 'attendance' } })}
              className="flex items-center gap-2 text-sm md:text-base text-muted-foreground hover:text-foreground transition-colors mb-4"
            >
              <Icon name="ArrowLeft" size={20} />
              <span>Back to Dashboard</span>
            </button>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">Punch Out</h1>
            <p className="text-sm md:text-base text-muted-foreground">Complete your work session with verification</p>
          </div>

          {validationError && (
            <div className="mb-4 md:mb-6 p-4 bg-error/10 border border-error/20 rounded-lg flex items-start gap-3">
              <Icon name="AlertCircle" size={20} className="text-error mt-0.5 flex-shrink-0" />
              <p className="text-sm md:text-base text-error">{validationError}</p>
            </div>
          )}

          {punchInData && (
            <div className="space-y-4 md:space-y-6">
              <PunchInSummary punchInData={punchInData} currentDuration={currentDuration} />

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                <CameraCapture onCapture={handleImageCapture} capturedImage={capturedImage} />
                <LocationVerification
                  punchInLocation={{
                    latitude: punchInData.latitude,
                    longitude: punchInData.longitude
                  }}
                  onLocationCapture={handleLocationCapture}
                  currentLocation={currentLocation}
                />
              </div>

              <WorkDurationSummary punchInTime={punchInData.punchInTime} currentTime={currentTime} />

              <div className="bg-card rounded-xl shadow-elevation-2 p-4 md:p-6 border border-border">
                <div className="flex items-start gap-3 mb-4 md:mb-6">
                  <Icon name="Info" size={20} className="text-primary mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-sm md:text-base font-semibold text-foreground mb-2">Before You Punch Out</h3>
                    <ul className="text-xs md:text-sm text-muted-foreground space-y-1">
                      <li>• Ensure your selfie is clear and well-lit</li>
                      <li>• Verify your location is accurate</li>
                      <li>• Check your total work duration</li>
                      <li>• Make sure you've completed all tasks</li>
                    </ul>
                  </div>
                </div>

                <Button
                  onClick={handlePunchOut}
                  disabled={!capturedImage || !currentLocation || isSubmitting}
                  loading={isSubmitting}
                  iconName="LogOut"
                  iconPosition="left"
                  fullWidth
                  size="lg"
                >
                  {isSubmitting ? 'Processing Punch Out...' : 'Confirm Punch Out'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default SelfiePunchOut;

